'use client';

import { Children, forwardRef, useCallback, useContext, useEffect, useState } from 'react';
import { type SwipeEventData, useSwipeable } from 'react-swipeable';

import { css, cx } from '@/styled-system/css';

import { useInertProps } from '../../hooks/use-inert-props';
import { PhotoCarouselRootContext } from './context/photo-carousel-root.context';
import { useSlidesRange } from './hooks/use-slides-range';
import { PhotoCarouselSlide } from './photo-carousel-slide';
import { photoCarouselSlidesStyles } from './styles/photo-carousel-slides.styles';
import type { PhotoCarouselSlidePropsInterface } from './types/photo-carousel-slide.types';
import type { PhotoCarouselSlidesPropsInterface } from './types/photo-carousel-slides.types';

/**
 * Slide container. Uses react-swipeable to make its children swipeable using either touch or a mouse.
 *
 * @since 8.66.0
 */

export const PhotoCarouselSlides = forwardRef<HTMLDivElement, PhotoCarouselSlidesPropsInterface>(
  (props: PhotoCarouselSlidesPropsInterface, forwardedRef) => {
    const {
      activeIndex: activeIndexContext,
      id: idContext,
      navigationDirection,
      setTotalSlides,
      shouldLoop = true,
      updateActiveIndex,
    } = useContext(PhotoCarouselRootContext);

    const {
      activeIndex = activeIndexContext || 0,
      children,
      className,
      css: cssProp,
      onSwiped,
      onSwiping,
      renderSlide = ({ key, slideProps }) => <PhotoCarouselSlide key={key} {...slideProps} />,
      shouldAnimateTranslations = true,
      totalSlides = Children.toArray(children).length || 0,
      ...rest
    } = props;

    const [swipingOffset, setSwipingOffset] = useState(0);

    /**
     * Because main logic for needing these props live below in a
     * `useCallback` we need to provide both `inertProps` as true/false
     * to call later. This is because you cannot use a hook inside a callback.
     */
    const inertPropsWhenHidden = useInertProps(true);
    const inertPropsWhenVisible = useInertProps(false);

    const translateLeft = () => {
      updateActiveIndex(activeIndex - 1);
    };

    const translateRight = () => {
      updateActiveIndex(activeIndex + 1);
    };

    // Called after the swipe.
    const handleSwiped = (swipeEventData: SwipeEventData) => {
      if (shouldTranslateOnSwiped(swipingOffset)) {
        if (swipingOffset > 0) {
          translateLeft();
        } else {
          translateRight();
        }
      }

      // Reset swipingOffset so slides snap into place.
      setSwipingOffset(0);

      onSwiped?.(swipeEventData);
    };

    // Called many times during the swipe.
    // Each time, update `swipingOffset` to allow the slide to move as it's being swiped.
    const handleSwiping = (swipeEventData: SwipeEventData) => {
      // Number of pixels swiped
      const delta = swipeEventData.deltaX;

      // The percent of a Property Card's width (as an integer) that the touch event has travelled.
      // 350 = average width of a property card
      const swipingOffset = 100 * (delta / 350);

      // Allow the slide to move as it's being swiped.
      setSwipingOffset(swipingOffset);

      onSwiping?.(swipeEventData);
    };

    // To trigger a full swipe, the touch event needs to travel at least 10% of the
    // width of the property card.
    const shouldTranslateOnSwiped = (swipingOffset: number) => {
      return Math.abs(swipingOffset) > 10;
    };

    const render = useCallback(
      ({
        slideIndex,
        slidePosition,
        virtualIndex,
      }: {
        slideIndex: number;
        slidePosition: 'left' | 'center' | 'right';
        virtualIndex: number;
      }) => {
        const key = `slide-${virtualIndex}`;

        const childrenArray = Array.isArray(children) ? children : [children];
        const isHidden = slidePosition !== 'center';
        const inertProps = isHidden ? inertPropsWhenHidden : inertPropsWhenVisible;

        const slideProps: PhotoCarouselSlidePropsInterface = {
          'aria-hidden': isHidden,
          'aria-label': `Slide ${slideIndex + 1} of ${totalSlides}`,
          'children': childrenArray?.[slideIndex],
          'id': `${idContext}-slide-${slideIndex + 1}`,
          shouldAnimateTranslations,
          slidePosition,
          ...inertProps,
        };

        return renderSlide({ slideProps, key });
      },
      [
        children,
        totalSlides,
        idContext,
        shouldAnimateTranslations,
        renderSlide,
        inertPropsWhenHidden,
        inertPropsWhenVisible,
      ],
    );

    const content = useSlidesRange({
      activeIndex,
      navigationDirection,
      render,
      shouldLoop,
      totalSlides,
    });

    const swipeHandlers = useSwipeable({
      delta: 1,
      onSwiped: handleSwiped,
      onSwiping: handleSwiping,
    });

    const styles = css(photoCarouselSlidesStyles, cssProp);

    useEffect(() => {
      setTotalSlides(totalSlides);
    }, [children, setTotalSlides, totalSlides]);

    // If there's only one slide, just render it without the `PhotoCarousel.Slides` and swipeable wrappers
    if (totalSlides < 2) {
      return renderSlide({ key: '', slideProps: { children } });
    }

    return (
      <div
        className={cx('c11n', 'c11n_photo-carousel__slides', styles, className)}
        data-c11n-component="PhotoCarousel.Slides"
        // Keeps the adjacent slides visible while a swipe is in progress; they are
        // otherwise `visibility: hidden` so screen readers and Tab cannot reach them.
        data-swiping={swipingOffset !== 0 ? '' : undefined}
        ref={forwardedRef}
        // Using inline style so Panda doesn't have to create new classes with each swipe.
        style={{ transform: `translate3d(${swipingOffset ?? 0}%, 0,0)` }}
        {...rest}
      >
        <div
          className="c11n c11n_photo-carousel__swipeable"
          data-c11n-component="PhotoCarousel.Swipeable"
          {...swipeHandlers}
        >
          {content}
        </div>
      </div>
    );
  },
);

PhotoCarouselSlides.displayName = 'PhotoCarousel.Slides';
